// ============================================================
// ModalEdadPaciente
// ------------------------------------------------------------
// Valida edad máxima del paciente y del prestador en la fecha de consulta:
// - Si en fechaConsulta el paciente tiene 120 años o menos: OK.
// - Si en fechaConsulta el paciente ya tiene 121 años: no debe guardar.
// - El modal preventivo avisa si con la fechaConsulta actual ya tiene 121.
// - El modal bloqueante se usa al intentar guardar.
// ============================================================

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./ModalEdicion.css";

export const calcularEdadPacienteEnFechaConsulta = (fechaNacimiento, fechaConsulta) => {
  if (!fechaNacimiento || !fechaConsulta) return null;

  const nacimientoStr = String(fechaNacimiento).split("T")[0];
  const consultaStr = String(fechaConsulta).split("T")[0];

  const [anioNac, mesNac, diaNac] = nacimientoStr.split("-").map(Number);
  const [anioCon, mesCon, diaCon] = consultaStr.split("-").map(Number);

  if (!anioNac || !mesNac || !diaNac || !anioCon || !mesCon || !diaCon) {
    return null;
  }

  let edad = anioCon - anioNac;

  if (
    mesCon < mesNac ||
    (mesCon === mesNac && diaCon < diaNac)
  ) {
    edad--;
  }

  return edad;
};

export const pacienteTieneEdadPermitida = (fechaNacimiento, fechaConsulta) => {
  const edad = calcularEdadPacienteEnFechaConsulta(fechaNacimiento, fechaConsulta);

  if (edad === null || edad === undefined) return true;

  return edad <= 120;
};

// Valida únicamente la edad máxima del prestador.

export const prestadorTieneEdadPermitida = (fechaNacimiento, fechaConsulta) => {
  const edad = calcularEdadPacienteEnFechaConsulta(
    fechaNacimiento,
    fechaConsulta
  );

  if (edad === null || edad === undefined) return true;

  return edad <= 90;
};

// ============================================================
// ESTADO DE EDAD PARA EL MODAL
// ------------------------------------------------------------
// Esta función conserva su nombre original para no afectar
// el funcionamiento actual del paciente.
//
// fechaNacimientoEvaluada:
// - Recibe paciente.fecha_nacimiento cuando se valida al paciente.
// - Recibe medico.fecha_nacimiento cuando se valida al prestador.
//
// limiteMaximo:
// - Por defecto es 120 para conservar la regla del paciente.
// - Se enviará 90 cuando el modal valide al prestador.
// ============================================================
export const obtenerEstadoEdadPaciente = (
  fechaNacimientoEvaluada,
  fechaConsulta,
  limiteMaximo = 120
) => {
  const edad = calcularEdadPacienteEnFechaConsulta(
    fechaNacimientoEvaluada,
    fechaConsulta
  );

  // Si no hay información suficiente, esta función no bloquea.
  // Las fechas obligatorias se validan previamente en cada formulario.
  if (edad === null || edad === undefined) {
    return {
      permitido: true,
      edad: null,
      estado: "ok",
    };
  }

  // El límite será 120 para paciente o 90 para prestador.
  if (edad <= limiteMaximo) {
    return {
      permitido: true,
      edad,
      estado: "ok",
    };
  }

  return {
    permitido: false,
    edad,
    estado: "bloqueado",
  };
};

const ModalEdadPaciente = ({
  fechaNacimiento,
  fechaConsulta,
  // Indica qué persona se está validando:
  // - "paciente": usa edad máxima de 120 años.
  // - "prestador": usa edad máxima de 90 años.
  // Se conserva "paciente" por defecto para no modificar
  // el comportamiento de las llamadas que ya existen.
  tipoPersona = "paciente",
  modo = "preventivo", // "preventivo" | "bloqueante"
  mostrar = true,
  onClose,
}) => {
  const navigate = useNavigate();
  const [cerradoPreventivoKey, setCerradoPreventivoKey] = useState(null);

  // La fecha recibida corresponde a la persona indicada en tipoPersona:
  // paciente.fecha_nacimiento o medico.fecha_nacimiento.
  // Aquí únicamente se selecciona el límite máximo aplicable.
  const esPrestador = tipoPersona === "prestador";
  const limiteMaximo = esPrestador ? 90 : 120;

  const resultado = useMemo(() => {
    return obtenerEstadoEdadPaciente(
      fechaNacimiento,
      fechaConsulta,
      limiteMaximo
    );
  }, [fechaNacimiento, fechaConsulta, limiteMaximo]);

  // tipoPersona forma parte de la llave para que los estados preventivos
  // del paciente y del prestador nunca se confundan entre sí.
  const keyActual =
    `${tipoPersona}|${fechaNacimiento || ""}|${fechaConsulta || ""}|${modo}`;

  const esBloqueante = modo === "bloqueante";
  const debeMostrar =
    mostrar &&
    !resultado.permitido &&
    (esBloqueante || cerradoPreventivoKey !== keyActual);

  useEffect(() => {
    if (resultado.permitido) {
      setCerradoPreventivoKey(null);
    }
  }, [resultado.permitido]);

  if (!debeMostrar) return null;

  const handleClose = () => {
    if (!esBloqueante) {
      setCerradoPreventivoKey(keyActual);
    }

    if (onClose) {
      onClose();
    }
  };

  return (
    <div className="modal-overlay modal-perfil-overlay">
      <div className="modal-content modal-perfil">
        <h3>
          {esBloqueante
            ? "⚠️ No se puede registrar la atención"
            : "⚠️ Revisa la fecha de consulta"}
        </h3>

        {/* 
        El mismo modal muestra contenido diferente según tipoPersona.
        La validación y el texto del paciente permanecen sin cambios.
        El prestador utiliza exclusivamente el límite máximo de 90 años.
      */}
        {esPrestador ? (
          <>
            <p>
              En la fecha de consulta capturada, el prestador ya tiene{" "}
              <strong>{resultado.edad} años</strong>.
            </p>

            <p>
              El sistema sólo permite registrar atenciones cuando el prestador
              tiene hasta <strong>90 años</strong>.
            </p>

            <p>
              Para continuar, ajusta la fecha de consulta si la atención ocurrió
              antes de que el prestador cumpliera 91 años y dentro del límite
              permitido de 48 horas.
            </p>
          </>
        ) : esBloqueante ? (
          <>
            <p>
              En la fecha de consulta capturada, el paciente ya tiene{" "}
              <strong>{resultado.edad} años</strong>.
            </p>

            <p>
              El sistema solo permite registrar pacientes de hasta{" "}
              <strong>120 años</strong>.
            </p>

            <p>
              Para continuar, ajusta la fecha de consulta si la atención ocurrió
              antes de que el paciente cumpliera 121 años y dentro del límite
              permitido de 48 horas.
            </p>
          </>
        ) : (
          <>
            <p>
              Con la fecha de consulta actual, el paciente ya tiene{" "}
              <strong>{resultado.edad} años</strong>.
            </p>

            <p>
              Si la atención ocurrió antes de que el paciente cumpliera 121 años,
              ajusta la fecha de consulta antes de guardar.
            </p>

            <p>
              Recuerda que solo se permite mover la fecha de consulta dentro del
              límite de 48 horas.
            </p>
          </>
        )}


        <div className="modal-buttons">
          <button className="btn-guardar" onClick={handleClose}>
            {esBloqueante ? "Corregir fecha" : "Entendido"}
          </button>

          <button className="btn-cancelar" onClick={() => navigate(-1)}>
            Regresar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalEdadPaciente;